import { RestRequest, RestContext, ResponseFunction } from 'msw'
import {
  addToEntityListData,
  updateEntityListData,
  deleteEntityListData,
} from '../../modules/mockDataCommon'
import {
  getCompanyMSServiceListDataByCMSId,
  updateMasterServiceInCompanyMSServices,
} from '../companyMSServices/mockData'
import { requireAuthenticatedUser } from '../../decorators'
import {
  getCompanyMasterServiceListData,
  getCompanyMasterServiceDataById,
  getPotentialLocationSelectItemListDataByCMSId,
  getCompanyDataByCompanyMasterServiceId,
  getSupplierListDataByCompanyMasterServiceId,
  propagateCompanyMasterServiceAdditions,
  propagateCompanyMasterServiceChanges,
  propagateCompanyMasterServiceDeletions,
} from './mockData'
import type { IEntitySelectItemWithOptionalAvatar } from '~/types/models'
import type { ICompany, ICompanyMasterService } from '~/types/company/models'

// async function getCompanyMasterServices(
//   req: RestRequest,
//   res: ResponseFunction,
//   ctx: RestContext
// ) {
//   console.log(`'Returning companymasterservices.`)
//   return res(ctx.status(200), ctx.json({ items: getCompanyMasterServiceListData() }))
// }

interface GetCompanyMasterServiceResponse {
  data: ICompanyMasterService
}

/**
 * GET one companyMasterService, with its companyMSServices nested.
 *
 * Part of the CRUD set the real API generates for every entity, and the shape
 * matches: the server's CompanyMasterService entity embeds
 * `company_m_s_services`, which is what makes this the right call for the
 * Company -> Service Group -> Service cascade in the Case dialog.
 *
 * NOT the `:id/companymsservices` route registered below. That one is
 * mock-only -- the server's companymasterservices resource adds exactly three
 * sub-routes (potentiallocations, company, suppliers) and no fourth -- so a
 * service built on it would work here and 404 against the real API.
 */
async function getCompanyMasterService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  const companyMasterService = await getCompanyMasterServiceDataById(id)
  if (!companyMasterService) {
    const responseBody = {
      statusCode: 404,
      message: `Can't find companyMasterService ${id}.`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
  const responseBody: GetCompanyMasterServiceResponse = {
    data: companyMasterService,
  }
  return res(ctx.status(200), ctx.json(responseBody))
}

async function getPotentialLocations(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(
    `Returning potential locations for service of companyMasterService id '${id}'.`
  )
  return res(
    ctx.status(200),
    ctx.json({
      data: await getPotentialLocationSelectItemListDataByCMSId(id),
    })
  )
}

interface GetCompanyFromCompanyMasterServiceResponse {
  data: ICompany
}

async function getCompanyFromCompanyMasterService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetCompanyFromCompanyMasterServiceResponse = {
      data: await getCompanyDataByCompanyMasterServiceId(id), // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Company from companyMasterService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

interface GetCompanyFromCompanyMasterServiceResponse {
  data: ICompany
}

interface GetSuppliersFromCompanyMasterServiceResponse {
  data: IEntitySelectItemWithOptionalAvatar[]
}

async function getSuppliersFromCompanyMasterService(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  try {
    const responseBody: GetSuppliersFromCompanyMasterServiceResponse = {
      data: await getSupplierListDataByCompanyMasterServiceId(id), // will either be valid or will have thrown error
    }
    return res(ctx.status(200), ctx.json(responseBody))
  } catch (e: unknown) {
    const responseBody = {
      statusCode: 404,
      message: `Can't get Suppliers from companyMasterService ${id}: ${e.message}`,
    }
    return res(ctx.status(404), ctx.json(responseBody))
  }
}

async function getCompanyMSServicesByCMSId(
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) {
  const { id } = req.params
  if (typeof id !== 'string') {
    const responseBody = {
      statusCode: 500,
      message: 'Passed ID is not a string.',
    }
    return res(ctx.status(500), ctx.json(responseBody))
  }
  console.log(`'Returning opportunityMSServices for opportunity "${id}".`)
  const data = await getCompanyMSServiceListDataByCMSId(id) // may be empty array. That's a valid response.
  return res(ctx.status(200), ctx.json({ data }))
}

// post

const postCompanyMasterService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return addToEntityListData(
    req,
    res,
    ctx,
    getCompanyMasterServiceListData,
    undefined,
    propagateCompanyMasterServiceAdditions
  )
}

const patchCompanyMasterService = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return updateEntityListData(req, res, ctx, getCompanyMasterServiceListData, [
    updateMasterServiceInCompanyMSServices,
    propagateCompanyMasterServiceChanges,
  ])
}

const deleteCompanyMasterServices = async (
  req: RestRequest,
  res: ResponseFunction,
  ctx: RestContext
) => {
  return deleteEntityListData(
    req,
    res,
    ctx,
    getCompanyMasterServiceListData,
    undefined,
    propagateCompanyMasterServiceDeletions
  )
}

export const handlers = [
  // {
  //   method: 'get',
  //   path: '/api/companymasterservices',
  //   handler: getCompanyMasterServices,
  // },
  {
    method: 'get',
    path: '/api/companymasterservices/:id/potentiallocations',
    handler: getPotentialLocations,
  },
  {
    method: 'get',
    path: '/api/companymasterservices/:id/company',
    handler: getCompanyFromCompanyMasterService,
  },
  {
    method: 'get',
    path: '/api/companymasterservices/:id/suppliers',
    handler: getSuppliersFromCompanyMasterService,
  },
  {
    method: 'get',
    path: '/api/companymasterservices/:id/companymsservices',
    handler: getCompanyMSServicesByCMSId,
  },
  {
    method: 'get',
    path: '/api/companymasterservices/:id',
    handler: getCompanyMasterService,
  },
  {
    method: 'post',
    path: '/api/companymasterservices',
    handler: postCompanyMasterService,
  },
  {
    method: 'patch',
    path: '/api/companymasterservices/:id',
    handler: patchCompanyMasterService,
  },
  {
    method: 'delete',
    path: '/api/companymasterservices/:id',
    handler: deleteCompanyMasterServices,
  },
].map((h) => ({ ...h, ...{ handler: requireAuthenticatedUser(h.handler) } }))
